import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { MapPin, Cloud, Sun, CloudRain, Wind, Search, Heart } from 'lucide-react'
import MoodWordCloud from '../components/MoodWordCloud'
import SearchBar from '../components/SearchBar'
import WeatherWidget from '../components/WeatherWidget'
import UserButton from '../components/UserButton'
import { useMusic } from '../contexts/MusicContext'
import { getWeatherData, getCurrentLocation } from '../services/weatherService'
import { getSpotifyAuthUrl } from '../services/musicService'

const HomePage = () => {
  const navigate = useNavigate()
  const { dispatch, mood, weather, location } = useMusic()
  const [isLoading, setIsLoading] = useState(false)
  const [customMood, setCustomMood] = useState('')

  useEffect(() => {
    // 获取用户位置和天气信息
    initializeLocationAndWeather()
  }, [])

  const initializeLocationAndWeather = async () => {
    try {
      const locationData = await getCurrentLocation()
      dispatch({ type: 'SET_LOCATION', payload: locationData })
      
      const weatherData = await getWeatherData(locationData.lat, locationData.lng)
      dispatch({ type: 'SET_WEATHER', payload: weatherData })
    } catch (error) {
      console.error('Error getting location/weather:', error)
    }
  }

  const handleMoodSelect = async (selectedMood) => {
    setIsLoading(true)
    dispatch({ type: 'SET_MOOD', payload: selectedMood })
    
    // 跳转到播放器页面并开始推荐
    navigate('/player', { 
      state: { 
        mood: selectedMood, 
        weather: weather, 
        location: location 
      } 
    })
  }

  const handleSpotifyConnect = () => {
    console.log('🎧 连接到 Spotify')
    const authUrl = getSpotifyAuthUrl()
    console.log('🔗 跳转到 Spotify 授权页面:', authUrl)
    window.location.href = authUrl
  }

  const handleCustomMoodSubmit = () => {
    if (customMood.trim()) {
      handleMoodSelect(customMood.trim())
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* 顶部导航栏 */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute top-0 left-0 right-0 z-10 p-4"
      >
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center space-x-2"
          >
            <span className="text-2xl">🎵</span>
            <span className="text-xl font-bold text-gray-800">自由音乐</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="flex items-center space-x-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/favorites')}
              className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-80 backdrop-blur-sm rounded-full shadow-lg border border-white border-opacity-20 hover:bg-opacity-90 transition-all duration-200"
            >
              <Heart className="w-4 h-4 text-gray-600" />
              <span className="hidden sm:inline text-gray-700">收藏</span>
            </motion.button>
            
            <UserButton />

            {/* Spotify 连接按钮 */}
            <motion.button
              onClick={handleSpotifyConnect}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors shadow-md"
            >
              <span className="text-lg">🎧</span>
              <span className="hidden sm:inline text-sm font-medium">连接 Spotify</span>
            </motion.button>
          </motion.div>
        </div>
      </motion.header>

      {/* 主要内容 */}
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-4 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -top-4 -right-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 text-center max-w-4xl mx-auto"
      >
        {/* 标题区域 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-12"
        >
          <h1 className="text-6xl font-bold text-gray-800 mb-4">
            🎵 自由音乐
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            基于你的心情与天气，发现属于你的音乐
          </p>
          
          {/* 天气信息 */}
          <WeatherWidget weather={weather} location={location} />
        </motion.div>

        {/* 心情选择区域 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-semibold text-gray-700 mb-8">
            选择你现在的心情
          </h2>
          
          <MoodWordCloud onMoodSelect={handleMoodSelect} isLoading={isLoading} />
        </motion.div>

        {/* 自定义心情输入 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center justify-center space-x-4 max-w-md mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="或者描述你的心情..."
                value={customMood}
                onChange={(e) => setCustomMood(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCustomMoodSubmit()}
                className="w-full pl-10 pr-4 py-3 rounded-full border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all duration-200 glass-effect"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCustomMoodSubmit}
              disabled={!customMood.trim() || isLoading}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-full font-medium hover:from-purple-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
            >
              开始
            </motion.button>
          </div>
        </motion.div>

        {/* 搜索功能 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="max-w-md mx-auto"
        >
          <SearchBar />
        </motion.div>
      </motion.div>

      {/* 加载状态 */}
      {isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-xl p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-700">正在为你寻找完美的音乐...</p>
          </div>
        </motion.div>
      )}
      
      </div>
    </div>
  )
}

export default HomePage
